/**
 * Client-side auth utilities
 * @package @app/auth
 *
 * IMPORTANT: basePath must match the server config in config.ts.
 * baseURL uses window.location.origin on client, env fallback on server/build.
 */

import {
  adminClient,
  magicLinkClient,
  organizationClient,
} from 'better-auth/client/plugins';
import { apiKeyClient } from '@better-auth/api-key/client';
import { stripeClient } from '@better-auth/stripe/client';
import { createAuthClient } from 'better-auth/react';

function getBaseURL(): string {
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  // Vite injects env vars into import.meta.env at build time; TS lib doesn't
  // model this, so access via a narrow local cast to avoid a repo-wide types dep.
  try {
    const viteEnv = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
    if (viteEnv?.VITE_BASE_URL) return viteEnv.VITE_BASE_URL;
  } catch {
    // import.meta.env not available in non-Vite contexts
  }
  return process.env.VITE_BASE_URL || 'http://localhost:3000';
}

export const authClient = createAuthClient({
  baseURL: getBaseURL(),
  basePath: '/api/auth',
  plugins: [
    magicLinkClient(),
    apiKeyClient(),
    adminClient(),
    organizationClient(),
    stripeClient({ subscription: true }),
  ],
});

export const { signIn, signOut, useSession, getSession } = authClient;

export const { magicLink, apiKey, admin, organization, subscription } = authClient;
